import React, {useEffect, useState} from 'react';
import styled from 'styled-components/native';
import {useTheme} from 'styled-components';
import {Keyboard, Platform, StatusBar} from 'react-native';
import TextGeneric from '../../../components/textGeneric';
import GenericButton from '../../../components/button';
import {Theme} from '../../../theme/lightTheme';
import MainLayout from '../../mainLayout';
import Animated, {FadeIn, FadeOut} from 'react-native-reanimated';
import {tr} from '../../../translations';
import TextInput from '../../../components/textInput';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {resetPassword} from './actions';
import {ScreenProps} from '../../../interfaces/screens/mainScreen';
import BackIcon from '../../../components/header/backIcon';
import screens from '../../../config/screens';
import useResponsiveDimensions from '../../../utils/hooks/useResponsiveDimensions';
import {useRoute, RouteProp} from '@react-navigation/native';

type ResetPasswordRouteParams = {
  resetPasswordPage: {
    email: string;
  };
};

const Container = styled.View<{height: number; paddingTop: number}>`
  width: 100%;
  height: ${({height}) => height}px;
  padding-top: ${({paddingTop}) => paddingTop}px;
`;

const ContentContainer = styled.View`
  width: 100%;
  flex: 1;
  align-items: center;
`;

const ImageContainer = styled(Animated.View)<{width: number}>`
  width: ${({width}) => width}px;
  aspect-ratio: 1.333;
  align-items: center;
  justify-content: center;
  align-self: center;
  overflow: hidden;
  margin-bottom: 2%;
`;

const Image = styled.Image`
  width: 100%;
  height: 100%;
  ${Platform.OS !== 'web' && 'resize-mode: contain;'}
`;

const BackIconContainer = styled.View<{topPosition: number}>`
  position: absolute;
  top: ${({topPosition}) => topPosition}px;
  background-color: ${({theme}) => theme.text.light};
  border-radius: 100px;
  align-items: center;
  justify-content: center;
  z-index: 99;
  shadow-color: ${({theme}) => theme.text.dark};
  shadow-offset: 2px 4px;
  shadow-opacity: 0.1;
  shadow-radius: 4px;
  elevation: 1;
`;

const Title = styled(TextGeneric)<{
  color: string;
  fontSize: number;
  paddingBottom: number;
}>`
  color: ${({color}) => color};
  font-size: ${({fontSize}) => fontSize}px;
  text-align: center;
  padding-bottom: ${({paddingBottom}) => paddingBottom}px;
`;

const SubTitle = styled(TextGeneric)<{fontSize: number; lineHeight: number}>`
  font-size: ${({fontSize}) => fontSize}px;
  color: ${({theme}) => theme.text.title};
  text-align: left;
  line-height: ${({lineHeight}) => lineHeight}px;
  margin-bottom: 3%;
`;

const SubmitButton = styled(GenericButton)<{
  width: number;
  backgroundColor: string;
}>`
  width: ${({width}) => width}px;
  border-radius: 100px;
  background-color: ${({backgroundColor}) => backgroundColor};
  margin-top: 20px;
`;

const ButtonContainer = styled.View<{width: string}>`
  width: ${({width}) => width};
  align-items: center;
`;

const ErrorText = styled(TextGeneric)<{
  color: string;
  fontSize: number;
  paddingTop: number;
}>`
  color: ${({color}) => color};
  font-size: ${({fontSize}) => fontSize}px;
  padding-top: ${({paddingTop}) => paddingTop}px;
  text-align: center;
`;

const ErrorContainer = styled.View<{minHeight: number}>`
  min-height: ${({minHeight}) => minHeight}px;
`;

const FormContainer = styled.View<{width: string}>`
  width: ${({width}) => width};
  gap: 16px;
  margin-bottom: 2%;
`;

const FormSection = styled.View<{width: string}>`
  width: ${({width}) => width};
  align-items: center;
`;

const ResetPassword: React.FC<ScreenProps> = ({navigation}) => {
  const theme = useTheme() as Theme;
  const {top: topInset, bottom: bottomInset} = useSafeAreaInsets();
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);

  // Get email from navigation params using useRoute hook
  const route = useRoute<RouteProp<ResetPasswordRouteParams, 'resetPasswordPage'>>();
  const email = route?.params?.email || '';

  // Use the responsive dimensions hook
  const {width, height, hdp, wdp, getByScreenSize, isLargeScreen} =
    useResponsiveDimensions();

  // Validate form
  useEffect(() => {
    const isCodeValid = code.trim().length >= 4;
    const isPasswordValid = password.length >= 6;
    const isConfirmValid = confirmPassword.length >= 6;
    const doPasswordsMatch = password === confirmPassword;

    setIsFormValid(
      isCodeValid && isPasswordValid && isConfirmValid && doPasswordsMatch,
    );

    // Show password mismatch error
    if (confirmPassword.length > 0 && !doPasswordsMatch) {
      setError(tr('resetPassword.passwordMismatch'));
    } else {
      setError('');
    }
  }, [code, password, confirmPassword]);

  // Custom back handler for web direct navigation
  const handleBackNavigation = () => {
    if (Platform.OS === 'web') {
      // Always navigate to login page on web
      navigation?.navigate(screens.auth.login);
    } else if (navigation?.goBack) {
      navigation.goBack();
    }
  };

  const resetPasswordHandler = async () => {
    setError('');
    Keyboard.dismiss();

    if (!isFormValid) {
      setError(tr('resetPassword.fillAllFields'));
      return;
    }

    const res = await resetPassword({
      email: (email || '').toLowerCase().trim(),
      token: code.trim(),
      password: password,
    });

    if (!res?.success && res?.message) {
      setError(res?.message);
    } else {
      // Navigate to login page on success
      handleBackNavigation();
    }
  };

  // Remove the existing dimensions and getResponsive functions
  const containerHeight =
    height -
    ((StatusBar.currentHeight || 0) + Math.max(bottomInset, height * 0.02));

  // Calculate responsive sizes - smaller icon to fit all elements
  const imageWidth = wdp(getByScreenSize(40, 12, 30));
  const formWidth = isLargeScreen ? '40%' : '100%';
  const submitButtonWidth = wdp(getByScreenSize(90, 20));

  // Fix for web where topInset can be 0
  const backIconTopPosition = Math.max(10, topInset + theme.text.s6);

  return (
    <MainLayout
      isCustomHeader
      showOverlay={false}
      noPaddingHeader
      keyboardAvoidScrollView>
      <Container
        height={containerHeight}
        paddingTop={Math.max(topInset, hdp(5))}>
        <Title
          color={theme.text.dark}
          fontSize={theme.text.s4}
          paddingBottom={hdp(5)}>
          {tr('screens.resetPasswordPage')}
        </Title>
        <BackIconContainer topPosition={backIconTopPosition}>
          <BackIcon
            tintColor={theme.secondary}
            canGoBack
            onPress={handleBackNavigation}
            iconName="back"
          />
        </BackIconContainer>
        <ContentContainer>
          <ImageContainer entering={FadeIn} width={imageWidth}>
            <Image
              resizeMode={'contain'}
              source={require('../../../assets/images/lock.png')}
            />
          </ImageContainer>
          <Animated.View entering={FadeIn.duration(700)}>
            <SubTitle fontSize={theme.text.s8} lineHeight={theme.text.s9 * 1.4}>
              {tr('resetPassword.subTitle')}
            </SubTitle>
          </Animated.View>
          <FormSection width={formWidth}>
            <FormContainer width={'100%'}>
              <TextInput
                label={tr('resetPassword.code')}
                placeholder={tr('resetPassword.codePlaceholder')}
                value={code}
                showLeftIcon
                leftIconName={'mail'}
                leftIconType={'Feather'}
                leftIconColor={theme.text.dark}
                onChangeText={text => setCode(text)}
                keyboardType={'default'}
                autoCapitalize={'characters'}
              />
              <TextInput
                label={tr('resetPassword.newPassword')}
                placeholder={tr('resetPassword.newPasswordPlaceholder')}
                value={password}
                showLeftIcon
                leftIconName={'lock'}
                leftIconType={'Feather'}
                leftIconColor={theme.text.dark}
                onChangeText={text => setPassword(text)}
                secureInput
              />
              <TextInput
                label={tr('resetPassword.confirmPassword')}
                placeholder={tr('resetPassword.confirmPasswordPlaceholder')}
                value={confirmPassword}
                showLeftIcon
                leftIconName={'lock'}
                leftIconType={'Feather'}
                leftIconColor={theme.secondary}
                onChangeText={text => setConfirmPassword(text)}
                secureInput
              />
            </FormContainer>
            <ErrorContainer minHeight={theme.text.s10 * 3}>
              {error && (
                <Animated.View entering={FadeIn} exiting={FadeOut}>
                  <ErrorText
                    color={theme.text.error}
                    fontSize={theme.text.s9}
                    paddingTop={hdp(2)}>
                    {error}
                  </ErrorText>
                </Animated.View>
              )}
            </ErrorContainer>
            <ButtonContainer width={'100%'}>
              <SubmitButton
                label={tr('resetPassword.submit')}
                radius={100}
                width={submitButtonWidth}
                onPress={resetPasswordHandler}
                disabled={!isFormValid}
                backgroundColor={!isFormValid ? theme.text.light : theme.primary}
                labelStyle={{
                  color: !isFormValid ? theme.secondary : theme.text.light,
                }}
              />
            </ButtonContainer>
          </FormSection>
        </ContentContainer>
      </Container>
    </MainLayout>
  );
};

export default ResetPassword;

